import { useEffect } from 'react';
import type { RefObject } from 'react';

import { buildClaimableRewards } from '../components/Dialogs/MinigamesDialog/buildRewards';
import { analytics } from '../services/analytics';
import { logToNewRelic } from '../services/debug';
import { intercom } from '../services/intercom';
import { useStores } from '../stores/useStores';
import { devError } from '../utils/log';

import { useCoinsAnimation } from './useCoinsAnimation';

export type MinigameMessagesOptions = {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  onAuthenticated: () => void;
  onClosed: () => void;
  onWarmReady?: (win: Window | null) => void;
};

export const useMinigameMessages = ({
  iframeRef,
  onAuthenticated,
  onClosed,
  onWarmReady,
}: MinigameMessagesOptions): void => {
  const { minigamesStore, userFreeSpinsStore, userStore } = useStores();
  const { startAnimation } = useCoinsAnimation();

  useEffect(() => {
    const eventHandler = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      try {
        const { type, payload } = event.data;
        switch (type) {
          case 'warmReady': {
            onWarmReady?.(event.source);
            break;
          }

          case 'gameReady': {
            void logToNewRelic(
              'MinigamesDialog game ready',
              'MinigamesDialog game ready: ' + JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'debug',
            );
            minigamesStore.setCurrentGameType(payload?.gameType);
            break;
          }
          case 'authenticationComplete': {
            onAuthenticated();
            break;
          }
          case 'authenticationError': {
            onAuthenticated();
            void logToNewRelic(
              'MinigamesDialog authentication error FE',
              'MinigamesDialog authentication error FE: ' +
                JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'error',
            );
            break;
          }
          case 'contactSupport': {
            intercom.handleOpenSupport();
            break;
          }
          case 'onErrorClose': {
            minigamesStore.closeMinigame();
            onClosed();
            break;
          }
          case 'closeGame': {
            minigamesStore.closeMinigame();
            onClosed();
            break;
          }
          case 'logMessage': {
            void logToNewRelic(
              'MinigamesDialog log message',
              'MinigamesDialog log message: ' + JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'debug',
            );
            break;
          }

          case 'playAnimation': {
            startAnimation({
              rewards: buildClaimableRewards(payload?.rewards),
              startMiniGames: false,
              hideOverlay: payload?.showBackdrop === false,
            });
            break;
          }
          case 'playAnimationAndClose': {
            startAnimation({
              rewards: buildClaimableRewards(payload?.rewards),
              hideOverlay: payload?.showBackdrop === false,
              onEnd: () => {
                minigamesStore.closeMinigame();
                onClosed();
              },
            });
            break;
          }
          case 'analyticsEvent': {
            // The minigame fires a distinctly-named event (the contract's event_name), not the legacy 'widget'
            // bucket. Every event through this handler is a minigame, so event_parent is the constant
            // 'minigames'; the game (e.g. 'crown_rise') rides as event_category. event_action/event_value/
            // button_text are properties. feature_type/feature_sub_type are intentionally not sent —
            // deprecated, the data team no longer consumes them.
            if (!payload?.event_name) {
              devError('MinigamesDialog analyticsEvent missing event_name', payload);
              break;
            }
            analytics
              .sendMiniGameEvent(payload.event_name, {
                event_parent: 'minigames',
                // currentGameType is nullable — coerce to undefined so the prop is omitted, not sent as null.
                event_category: payload?.minigame_name || minigamesStore.currentGameType || undefined,
                event_action: payload?.event_action,
                event_type: payload?.event_type,
                event_value: payload?.event_value,
                button_text: payload?.button_text,
              })
              .catch((err) => devError('MinigamesDialog analyticsEvent sendEvent failed', err));
            break;
          }
          case 'vibrate': {
            if (window.navigator.vibrate) {
              const duration = payload?.haptic === 'soft' ? 20 : payload?.haptic === 'hard' ? 100 : 50;
              window.navigator.vibrate(duration);
            }
            break;
          }
          case 'gameError': {
            void logToNewRelic(
              'MinigamesDialog gameError',
              'MinigamesDialog gameError: ' + JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'error',
            );
            userFreeSpinsStore.markErrored(payload?.errorCode);
            minigamesStore.closeMinigame(true);
            userFreeSpinsStore.closeDialog();
            onClosed();
            break;
          }
        }
      } catch (e) {
        devError('MinigamesDialog eventHandler error', e);
      }
    };

    window.addEventListener('message', eventHandler);

    return () => {
      window.removeEventListener('message', eventHandler);
    };
  }, [
    iframeRef,
    onAuthenticated,
    onClosed,
    onWarmReady,
    minigamesStore,
    startAnimation,
    userFreeSpinsStore,
    userStore.profile?.userId,
  ]);
};
